/**
 * Gift-intent store — Gifting Flavor A (web).
 *
 * Marks specific cart lines as gifts for an existing-account recipient.
 * Keyed by the SERVER cart-row id (orderId — the same id sent in the order
 * `items[]` and echoed as `gifts[].cart_item`). Deliberately NOT stored on
 * the anonymous cart: AnonCartItem carries only a client `localId`, no server
 * id, so gifting is an authenticated-only path and no third-party phone is ever
 * written to lvs_anon_cart pre-consent (LGPD).
 *
 * The value is the recipient's phone, normalized to +55… (formatBrazilianPhone).
 * The order payload is the server-side source of truth; this store only
 * survives reload/navigation so a marked cart line stays marked until checkout.
 *
 * Storage key: lvs_gift_intent  →  { [cartRowId]: recipientPhone }
 */

export type GiftIntentMap = Record<string, string>;

const GIFT_INTENT_KEY = "lvs_gift_intent";

/** Simple event bus — fires whenever the gift-intent map changes. Mirrors anonCartBus. */
export const giftIntentBus = {
  _listeners: [] as (() => void)[],
  fire() {
    this._listeners.forEach((fn) => fn());
  },
  subscribe(fn: () => void) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter((f) => f !== fn);
    };
  },
};

export function getGiftIntents(): GiftIntentMap {
  try {
    const raw = localStorage.getItem(GIFT_INTENT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as GiftIntentMap) : {};
  } catch {
    return {};
  }
}

/** Recipient phone for a cart line, or null if the line is not marked as a gift. */
export function getGiftIntent(cartRowId: number | string): string | null {
  const map = getGiftIntents();
  return map[String(cartRowId)] ?? null;
}

function persist(map: GiftIntentMap): void {
  try {
    localStorage.setItem(GIFT_INTENT_KEY, JSON.stringify(map));
    giftIntentBus.fire();
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

/** Mark a cart line as a gift for `phone`. An empty/nullish phone unmarks it. */
export function setGiftIntent(
  cartRowId: number | string,
  phone: string | null | undefined,
): GiftIntentMap {
  const map = getGiftIntents();
  const key = String(cartRowId);
  if (phone && phone.trim()) {
    map[key] = phone.trim();
    // A line is addressed (Flavor A) XOR share-by-link (Flavor C). The order
    // serializer rejects a cart_item that appears in both gifts[] and
    // gift_links[] (CART_ITEM_INVALID), so the two stores are kept disjoint here.
    clearGiftLinkIntent(key);
  } else {
    delete map[key];
  }
  persist(map);
  return map;
}

/** Unmark a single cart line (e.g. the recipient input was cleared). */
export function clearGiftIntent(cartRowId: number | string): GiftIntentMap {
  const map = getGiftIntents();
  const key = String(cartRowId);
  if (key in map) {
    delete map[key];
    persist(map);
  }
  return map;
}

/** Drop both intent stores — call after a successful order so nothing rides along next time. */
export function clearAllGiftIntents(): void {
  try {
    localStorage.removeItem(GIFT_INTENT_KEY);
    localStorage.removeItem(GIFT_LINK_INTENT_KEY);
    giftIntentBus.fire();
  } catch {
    // fail silently
  }
}

/**
 * Drop intents whose cart row is no longer present (deleted or expired lines),
 * so a stale recipient can't ride along on a future order. Pass the current
 * server cart-row ids. Returns true if anything was pruned.
 */
export function pruneGiftIntents(validCartRowIds: Array<number | string>): boolean {
  const map = getGiftIntents();
  const valid = new Set(validCartRowIds.map(String));
  let changed = false;
  for (const key of Object.keys(map)) {
    if (!valid.has(key)) {
      delete map[key];
      changed = true;
    }
  }
  if (changed) persist(map);
  return changed;
}

// --- Share-by-link gift intents — Gifting Flavor C --------------------------
//
// A Flavor C line has NO addressee: it mints an UNADDRESSED gift whose token is
// handed back to the sender so they can share a /presente/{token} link. Whoever
// opens it first claims it. Keyed by the server cart-row id (the same id sent in
// the order `items[]` and echoed as `gift_links[]`).
//
// Each entry carries the sender's OPTIONAL, PRIVATE organizer metadata:
//   - `label`   a note-to-self ("Ana") shown only in the sender's own outbox.
//               It NAMES A THIRD PARTY but never reaches them — a link is a bearer
//               token; the label has no bearing on who can claim, and is not a
//               phone/recipient. Threads onto gift_links[].label (backend cap 50).
//   - `message` the note shown on the public claim page. Threads onto
//               gift_links[].message (backend cap 200; the UI caps at 50).
// Both default to '' — an entry with no metadata is still a valid link intent
// (its key's mere presence is what marks the line as share-by-link).
//
// Disjoint from lvs_gift_intent by construction — the setters below and
// setGiftIntent() clear each other. Authenticated-only for the same LGPD reason
// as Flavor A: anon cart lines carry no server id.
//
// Storage key: lvs_gift_link_intent  →  { [cartRowId]: { label?, message? } }

const GIFT_LINK_INTENT_KEY = "lvs_gift_link_intent";

export interface GiftLinkMeta {
  /** Sender-private note-to-self naming the intended person. Never sent to them. */
  label?: string;
  /** Personal note rendered on the public claim page. */
  message?: string;
}

export type GiftLinkIntentMap = Record<string, GiftLinkMeta>;

/** Coerce an arbitrary stored value to a clean {label?, message?}. */
function sanitizeMeta(v: unknown): GiftLinkMeta {
  const meta: GiftLinkMeta = {};
  if (v && typeof v === "object") {
    const label = (v as any).label;
    const message = (v as any).message;
    if (typeof label === "string") meta.label = label;
    if (typeof message === "string") meta.message = message;
  }
  return meta;
}

/** The full link-intent map. Migrates the legacy `string[]` shape in place. */
export function getGiftLinkIntentMap(): GiftLinkIntentMap {
  try {
    const raw = localStorage.getItem(GIFT_LINK_INTENT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Legacy shape: a flat list of ids with no metadata (pre-composer). A row was
    // either "a link" or not; migrate each id to an empty-metadata entry.
    if (Array.isArray(parsed)) {
      const map: GiftLinkIntentMap = {};
      parsed.forEach((id) => {
        map[String(id)] = {};
      });
      return map;
    }
    if (parsed && typeof parsed === "object") {
      const map: GiftLinkIntentMap = {};
      for (const [k, v] of Object.entries(parsed)) {
        map[String(k)] = sanitizeMeta(v);
      }
      return map;
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * The cart-row ids marked as share-by-link gifts. Kept as a `string[]` (not the
 * map) so every existing membership check (`.includes`) is unchanged by the
 * metadata reshape.
 */
export function getGiftLinkIntents(): string[] {
  return Object.keys(getGiftLinkIntentMap());
}

/** True when this cart line is marked as a share-by-link gift. */
export function isGiftLinkIntent(cartRowId: number | string): boolean {
  return String(cartRowId) in getGiftLinkIntentMap();
}

/** The sender's private metadata for a link line, or null if it is not a link. */
export function getGiftLinkMeta(cartRowId: number | string): GiftLinkMeta | null {
  const map = getGiftLinkIntentMap();
  const key = String(cartRowId);
  return key in map ? map[key] : null;
}

function persistLinks(map: GiftLinkIntentMap): void {
  try {
    localStorage.setItem(GIFT_LINK_INTENT_KEY, JSON.stringify(map));
    giftIntentBus.fire();
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

/** Unmark a single cart line as a share-by-link gift (drops its metadata too). */
export function clearGiftLinkIntent(cartRowId: number | string): GiftLinkIntentMap {
  const map = getGiftLinkIntentMap();
  const key = String(cartRowId);
  if (key in map) {
    delete map[key];
    persistLinks(map);
  }
  return map;
}

/** Mark (or unmark) a cart line as a share-by-link gift. Clears any addressed intent. */
export function setGiftLinkIntent(
  cartRowId: number | string,
  isLink: boolean,
): GiftLinkIntentMap {
  const key = String(cartRowId);
  if (!isLink) return clearGiftLinkIntent(key);
  // Enforce the A-xor-C invariant (see setGiftIntent).
  clearGiftIntent(key);
  const map = getGiftLinkIntentMap();
  // Preserve any metadata a prior mark left behind — toggling link mode off and
  // on again in the cart must not silently wipe a message the sender wrote.
  if (!(key in map)) {
    map[key] = {};
    persistLinks(map);
  }
  return map;
}

/**
 * Merge the sender's private metadata onto a link line. Upserts the entry, so
 * writing a label/message also marks the line as a link (the two always travel
 * together — the picker and the cart set them at the same moment).
 */
export function setGiftLinkMeta(
  cartRowId: number | string,
  patch: GiftLinkMeta,
): GiftLinkIntentMap {
  const key = String(cartRowId);
  // Metadata implies the line is a link — keep it disjoint from the addressed store.
  clearGiftIntent(key);
  const map = getGiftLinkIntentMap();
  const next: GiftLinkMeta = { ...(map[key] ?? {}) };
  if (patch.label !== undefined) next.label = patch.label;
  if (patch.message !== undefined) next.message = patch.message;
  map[key] = next;
  persistLinks(map);
  return map;
}

/** Drop link intents whose cart row is gone. Returns true if anything was pruned. */
export function pruneGiftLinkIntents(validCartRowIds: Array<number | string>): boolean {
  const map = getGiftLinkIntentMap();
  const valid = new Set(validCartRowIds.map(String));
  let changed = false;
  for (const key of Object.keys(map)) {
    if (!valid.has(key)) {
      delete map[key];
      changed = true;
    }
  }
  if (changed) persistLinks(map);
  return changed;
}

// --- "Awaiting link" marker (the paid-but-no-gift corollary) ----------------
//
// A paid order does NOT guarantee a Gift exists: the cota join-gate can refuse
// at PIX settlement (CartJoinGateRefused → manual refund), and gift minting is
// additionally wrapped in a try/except that preserves the paid ticket. So the
// sender's link-ready screen must never assume a token is coming.
//
// Written at checkout with the number of link lines paid for; cleared as soon as
// that many live link gifts appear in GET /api/gift/sent/. If they never do, the
// sent-gifts screen degrades to an explicit "couldn't prepare this gift" state
// rather than rendering a broken /presente/null link.
//
// Storage key: lvs_gift_link_awaiting  →  { count, createdAt }

const GIFT_LINK_AWAITING_KEY = "lvs_gift_link_awaiting";

export interface GiftLinkAwaiting {
  count: number;
  createdAt: number;
  /**
   * The order these links belong to. Absent between checkout assembly and the moment
   * the server hands back an order id — the marker is written before submitOrder
   * resolves. A consumer that shows per-order UI must require a MATCH, never merely
   * the marker's presence: several screens navigate to unrelated orders with
   * `?fromCart=true`, and this marker lives for a 15-minute grace window.
   */
  orderId?: string;
}

export function markGiftLinksAwaiting(count: number): void {
  if (count <= 0) return;
  try {
    const payload: GiftLinkAwaiting = { count, createdAt: Date.now() };
    localStorage.setItem(GIFT_LINK_AWAITING_KEY, JSON.stringify(payload));
  } catch {
    // fail silently
  }
}

/**
 * Bind the pending marker to the order that carries its link gifts. Idempotent, and a
 * no-op when no marker is pending or one is already bound — a later, unrelated order
 * must never steal an earlier order's marker.
 */
export function attachGiftLinksAwaitingOrder(orderId: string): void {
  if (!orderId) return;
  const awaiting = getGiftLinksAwaiting();
  if (!awaiting || awaiting.orderId) return;
  try {
    const payload: GiftLinkAwaiting = { ...awaiting, orderId };
    localStorage.setItem(GIFT_LINK_AWAITING_KEY, JSON.stringify(payload));
  } catch {
    // fail silently
  }
}

export function getGiftLinksAwaiting(): GiftLinkAwaiting | null {
  try {
    const raw = localStorage.getItem(GIFT_LINK_AWAITING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.count === "number" && typeof parsed.createdAt === "number") {
      return parsed as GiftLinkAwaiting;
    }
    return null;
  } catch {
    return null;
  }
}

export function clearGiftLinksAwaiting(): void {
  try {
    localStorage.removeItem(GIFT_LINK_AWAITING_KEY);
  } catch {
    // fail silently
  }
}

// --- "Start a gift" nudge flag (game-card 🎁 → cart) ------------------------
// Set when the user taps the 🎁 affordance on a game/bolão card; consumed once
// on the next /cart mount to surface the per-line gift toggle. sessionStorage so
// it survives the add-flow navigation hops but not a new tab/session.
const GIFT_START_KEY = "lvs_start_gift";

export function markGiftStart(): void {
  try {
    sessionStorage.setItem(GIFT_START_KEY, "1");
  } catch {
    // fail silently
  }
}

export function consumeGiftStart(): boolean {
  try {
    if (sessionStorage.getItem(GIFT_START_KEY)) {
      sessionStorage.removeItem(GIFT_START_KEY);
      return true;
    }
  } catch {
    // fail silently
  }
  return false;
}

// --- First-send marker (inline education, not a coachmark) -----------------
//
// The send flow explains the mechanic ("a pessoa recebe um link e resgata a cota")
// in full on a sender's FIRST send, and tersely after. Set once, at the checkout
// that actually pays for link gifts — not when the flow is merely opened, so an
// abandoned first attempt still gets the full explanation next time.
//
// localStorage (not sessionStorage): "have I ever done this" must outlive the tab.
// A cleared browser re-teaches, which is the harmless direction to fail.
//
// Storage key: lvs_gift_first_send  →  "1"
const GIFT_FIRST_SEND_KEY = "lvs_gift_first_send";

/** True once the user has completed a checkout containing at least one link gift. */
export function hasSentGiftBefore(): boolean {
  try {
    return localStorage.getItem(GIFT_FIRST_SEND_KEY) === "1";
  } catch {
    // Treat an unreadable store as "never sent" — over-explaining beats under-explaining.
    return false;
  }
}

export function markGiftSent(): void {
  try {
    localStorage.setItem(GIFT_FIRST_SEND_KEY, "1");
  } catch {
    // fail silently
  }
}

// --- Gifter acceptance-toast tracking --------------------------------------
// Snapshot of gift ids the gifter has already been toast-notified as accepted,
// so a "🎉 X aceitou seu presente!" fires once per acceptance on the sent-gifts
// list. localStorage so it persists across sessions (unlike the start flag).
const GIFT_SEEN_ACCEPTED_KEY = "lvs_gift_seen_accepted";

export function getSeenAcceptedGifts(): number[] {
  try {
    const raw = localStorage.getItem(GIFT_SEEN_ACCEPTED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function markAcceptedGiftsSeen(ids: number[]): void {
  try {
    localStorage.setItem(GIFT_SEEN_ACCEPTED_KEY, JSON.stringify(ids));
  } catch {
    // fail silently
  }
}
